import { QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate, Link } from "react-router-dom";
import { session } from "@common/auth/session";
import { SessionProvider, useSession } from "@jkast90/kast-common/auth";
import { useCacheScopedToUser } from "@jkast90/kast-common/query-client";
import { logger } from "@jkast90/kast-common/diagnostics";
import { useRecordNavigation } from "@jkast90/kast-common/diagnostics/react";
import Banner from "./components/Banner";
import ErrorBoundary from "@jkast90/kast-common/error-boundary";
import { buttonClass, Spinner, LinkProvider } from "@jkast90/kast-common/ui";
import Footer from "./components/Footer";
import { AppSettings } from "@common/account";
import Header from "./components/Header";
import AboutPage from "./pages/AboutPage";
import AccountPage from "./pages/AccountPage";
import BugReportPage from "./pages/BugReportPage";
import HomePage from "./pages/HomePage";
import { queryClient } from "./queryClient";

// Loaded on first visit to /admin, so public visitors don't download the
// shared Table and its styles.
const AdminPage = lazy(() => import("./pages/AdminPage"));

/** The whole path is the screen: the admin tabs and the pages under them show in the report. */
const wholePath = (pathname: string) => pathname;

function Shell() {
  const { user } = useSession();
  useCacheScopedToUser(queryClient, user?.sub ?? null);
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const section = pathname.split("/")[1];

  // A router link to /#ships changes the URL but the browser doesn't scroll
  // to it, so do it here, from another page too.
  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [pathname, hash]);

  // The pages leading up to a bug report, deep links and back/forward included.
  useRecordNavigation(pathname, wholePath);

  return (
    <>
      <Header />
      <AppSettings />
      <Banner />
      <ErrorBoundary
        key={section}
        onError={(error, info) => logger.error(`[Crash] ${error.message}`, info.componentStack ?? "")}
        onReport={(crashNote) => navigate("/bug-report", { state: { crashNote } })}
        buttonClassName={buttonClass()}
        primaryButtonClassName={buttonClass({ variant: "primary" })}
      >
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route
            path="/admin"
            element={
              <Suspense fallback={<Spinner size="sm" showLabel />}>
                <AdminPage />
              </Suspense>
            }
          />
          <Route path="/bug-report" element={<BugReportPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ErrorBoundary>
      <Footer />
    </>
  );
}

export default function App() {
  return (
    <SessionProvider session={session}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <LinkProvider link={Link}>
            <Shell />
          </LinkProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </SessionProvider>
  );
}
